import Select from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';
import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';
 import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';

/** =========================
 * INNER REQUEST SHAPE
 * ========================= */
type SupplierPurLinkedRequestDto = {
  supplierId: string | null; // REQUIRED
  fromDate?: string | null;
  toDate?: string | null;
  organizationId?: string | null;
  includePurEffect0?: boolean;
  purCodeLike?: string | null;
  invCodeLike?: string | null;
  invoicePostedFlag?: boolean | null;
  invoiceCancelledFlag?: boolean | null;
  invoiceHoldFlag?: boolean | null;
  invoiceCodeLike?: string | null;
  invoiceNumLike?: string | null;
  supplierInvoiceNumLike?: string | null;
  paymentStatus?: string | null;
  paymentTypeId?: string | null;
  paymentCodeLike?: string | null;
  unlinkedSplitInvoices?: boolean;
  pageIndex?: number;
  pageSize?: number;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  rowType: string | null;
  branchId: string | null;
  branchName: string | null;
  branchName2: string | null;
  purId: string | null;
  purCode: string | null;
  purDate: string | null;
  purAmount: number;
  receiptList: string | null;
  invoiceList: string | null;
  paymentList: string | null;
  receiptsCount: number;
  invoicesCount: number;
  paymentsCount: number;
  invoicesTotal: number;
  paymentsTotal: number;
};

const REPORT_KEY = 'RPT_SupplierPurLinkedGrouped';
const ENDPOINT = 'FinReports/GetSupplierPurLinkedGrouped';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number | null | undefined, digits: number = 2) => {
  if (num === null || num === undefined) return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(num);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

// --- Helpers لتنسيق القوائم المدمجة (Receipts, Invoices, Payments) ---
const renderListBadges = (listStr: string | null, type: 'receipt' | 'invoice' | 'payment') => {
  if (!listStr) return <span className='text-muted small'>—</span>;

  // نفترض أن العناصر مفصولة بفاصلة في حال كان هناك أكثر من عنصر
  const items = listStr.split(',');

  return (
    <div className='d-flex flex-column gap-1'>
      {items.map((item, i) => {
        // الاستلامات تأتي بصيغة GUID_Code
        // الفواتير تأتي بصيغة Code_Amount
        const parts = item.split('_');
        let displayStr = item;

        if (type === 'receipt' && parts.length > 1) {
          displayStr = parts[parts.length - 1]; // أخذ الكود (آخر جزء)
        } else if (type === 'invoice' && parts.length > 1) {
          displayStr = `${parts[0]} (${formatNumber(Number(parts[1]))})`; // دمج الكود مع المبلغ
        } else if (type === 'payment' && parts.length > 1) {
          displayStr = `${parts[0]} (${formatNumber(Number(parts[1]))})`;
        }

        const badgeClass =
          type === 'receipt'
            ? 'bg-info text-info'
            : type === 'invoice'
              ? 'bg-warning text-warning'
              : 'bg-success text-success';

        return (
          <span
            key={i}
            className={`badge bg-opacity-10 border border-opacity-25 ${badgeClass}`}
            style={{ width: 'fit-content' }}
          >
            {displayStr}
          </span>
        );
      })}
    </div>
  );
};

const SupplierPurLinkedGrouped_Paged = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isAr = i18n.language !== 'en';

  // options
  const { OperatingUnitOptions: OptionsBranches, loading: loadingBranches ,  } =
    useGetOperatingUnitWithPermission();
  const { OptionSuppliers: OptionsSuppliers, loading: loadingSuppliers } = useGetSuppliersList();

  // Filters state
  const [supplierFilter, setSupplierFilter] = useState<any>(null); // إلزامي
  const [organizationFilter, setOrganizationFilter] = useState<any>(null);

  // Date range
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  // Additional textual/boolean Filters
  const [purCodeLike, setPurCodeLike] = useState<string>('');
  const [invoiceCodeLike, setInvoiceCodeLike] = useState<string>('');
  const [unlinkedSplitInvoices, setUnlinkedSplitInvoices] = useState<boolean>(false);

  const [showExport, setShowExport] = useState(false);

  // report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;

  const exportColumns: ExportColumn<RowDto>[] = [
    {
      headerAr: 'الفرع',
      headerEn: 'Branch',
      value: r => (isAr ? r.branchName2 || r.branchName : r.branchName || r.branchName2) || ''
    },
    { headerAr: 'رقم الطلب', headerEn: 'PUR Code', value: r => r.purCode || '' },
    { headerAr: 'تاريخ الطلب', headerEn: 'PUR Date', value: r => formatDate(r.purDate) },
    { headerAr: 'قيمة الطلب', headerEn: 'PUR Amount', value: r => r.purAmount || 0 },
    { headerAr: 'الاستلامات', headerEn: 'Receipts', value: r => r.receiptList || '0' },
    { headerAr: 'قيمة الفواتير', headerEn: 'Invoices Total', value: r => r.invoicesTotal || 0 },
    { headerAr: 'تفاصيل الفواتير', headerEn: 'Invoices List', value: r => r.invoiceList || '' },
    { headerAr: 'المدفوعات', headerEn: 'Payments Total', value: r => r.paymentsTotal || 0 }
  ];

  useEffect(() => {
    const initialRequest: SupplierPurLinkedRequestDto = {
      supplierId: null, // لا يوجد مورد مبدئياً
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
        // skipInitialFetch: true // تعطيل الجلب التلقائي لضمان اختيار المورد أولاً
      })
    );
  }, [dispatch]);

  const applyFilters = () => {
    const suppId = getOptId(supplierFilter);
    if (!suppId) return; // حماية إضافية

    const orgId = getOptId(organizationFilter);
    const prevReq = (((report as any)?.request ?? {}) as SupplierPurLinkedRequestDto) || {};

    const nextReq: SupplierPurLinkedRequestDto = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      supplierId: suppId,
      organizationId: orgId,
      fromDate: isoOrNull(fromDate),
      toDate: isoOrNull(toDate),
      purCodeLike: purCodeLike || null,
    //   invoiceCodeLike: invoiceCodeLike || null,
      invoiceNumLike: invoiceCodeLike || null,
      unlinkedSplitInvoices: unlinkedSplitInvoices || undefined
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setSupplierFilter(null);
    setOrganizationFilter(null);
    setFromDate(null);
    setToDate(null);
    setPurCodeLike('');
    setInvoiceCodeLike('');
    setUnlinkedSplitInvoices(false);

    const resetReq: SupplierPurLinkedRequestDto = {
      supplierId: null,
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
  };

  const totals = useMemo(() => {
    return items.reduce(
      (acc, row) => {
        acc.totalPurAmount += row.purAmount || 0;
        acc.totalInvoices += row.invoicesTotal || 0;
        acc.totalPayments += row.paymentsTotal || 0;
        return acc;
      },
      { totalPurAmount: 0, totalInvoices: 0, totalPayments: 0 }
    );
  }, [items]);

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center mb-2'>
          {/* المورد (إلزامي) */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-truck text-danger'></i>
                <span className='text-danger ms-1'>*</span>
              </span>
              <Select
                isLoading={loadingSuppliers}
                value={supplierFilter}
                onChange={setSupplierFilter}
                options={OptionsSuppliers}
                placeholder={isAr ? 'المورد' : 'Supplier '}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* الفرع / المنظمة */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-building text-primary'></i>
              </span>
              <Select
                isLoading={loadingBranches}
                value={organizationFilter}
                onChange={setOrganizationFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع ' : 'Organization'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* من تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* إلى تاريخ */}
          <div className='col-lg-2'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>
        </div>

        <div className='row g-2 align-items-center'>
          {/* بحث برقم الطلب */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-search'></i>
              </span>
              <input
                type='text'
                className='form-control'
                value={purCodeLike}
                onChange={e => setPurCodeLike(e.target.value)}
                placeholder={isAr ? 'رقم أمر الشراء' : 'PUR Code'}
              />
            </div>
          </div>

          {/* بحث برقم الفاتورة */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-file-invoice'></i>
              </span>
              <input
                type='text'
                className='form-control'
                value={invoiceCodeLike}
                onChange={e => setInvoiceCodeLike(e.target.value)}
                placeholder={isAr ? 'رقم الفاتورة' : 'Invoice Code'}
              />
            </div>
          </div>

          {/* فواتير غير مربوطة */}
          {/* <div className='col-lg-2'>
            <div className='form-check form-switch pt-1'>
              <input
                className='form-check-input'
                type='checkbox'
                id='unlinkedSplitInvoices'
                checked={unlinkedSplitInvoices}
                onChange={e => setUnlinkedSplitInvoices(e.target.checked)}
              />
              <label className='form-check-label small fw-bold' htmlFor='unlinkedSplitInvoices'>
                {isAr ? 'الفواتير غير المربوطة' : 'Unlinked Invoices'}
              </label>
            </div>
          </div> */}

          {/* Buttons */}
          <div className='col-lg-6 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
              disabled={!supplierFilter}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button
              className='btn btn-theme me-2'
              type='button'
              onClick={applyFilters}
              title={t('ApplyFilters')}
              disabled={!supplierFilter}
            >
              <i className='fas fa-filter fa-fw'></i> {isAr ? 'بحث' : 'Search'}
            </button>

            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading || items.length === 0}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>
      </CardHeader>

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className='table table-hover table-bordered mb-0'>
              <thead className='sticky-top align-middle' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small' rowSpan={2} style={{ minWidth: '130px' }}>
                    {isAr ? 'الفرع' : 'Branch'}
                  </th>
                  <th className='small text-primary' colSpan={3}>
                    {isAr ? 'أوامر الشراء' : 'Purchase Order Info'}
                  </th>
                  <th className='small text-info' colSpan={2}>
                    {isAr ? 'الاستلامات' : 'Receipts'}
                  </th>
                  <th className='small text-warning' colSpan={3}>
                    {isAr ? 'الفواتير' : 'Invoices'}
                  </th>
                  <th className='small text-success' colSpan={3}>
                    {isAr ? 'المدفوعات' : 'Payments'}
                  </th>
                </tr>
                <tr className='bg-theme bg-opacity-25 text-center' style={{ backdropFilter: 'blur(20px)' }}>
                  {/* PO Info */}
                  <th className='small' style={{ minWidth: '110px' }}>
                    {isAr ? 'رقم الأمر' : 'PUR Code'}
                  </th>
                  <th className='small' style={{ minWidth: '95px' }}>
                    {t('Date')}
                  </th>
                  <th className='small text-end' style={{ minWidth: '100px' }}>
                    {isAr ? 'المبلغ' : 'Amount'}
                  </th>

                  {/* Receipts */}
                  <th className='small' style={{ minWidth: '60px' }}>
                    {isAr ? 'العدد' : 'Count'}
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    {isAr ? 'التفاصيل' : 'Details'}
                  </th>

                  {/* Invoices */}
                  <th className='small' style={{ minWidth: '60px' }}>
                    {isAr ? 'العدد' : 'Count'}
                  </th>
                  <th className='small text-end' style={{ minWidth: '100px' }}>
                    {isAr ? 'الإجمالي' : 'Total'}
                  </th>
                  <th className='small' style={{ minWidth: '180px' }}>
                    {isAr ? 'التفاصيل' : 'Details'}
                  </th>

                  {/* Payments */}
                  <th className='small' style={{ minWidth: '60px' }}>
                    {isAr ? 'العدد' : 'Count'}
                  </th>
                  <th className='small text-end' style={{ minWidth: '100px' }}>
                    {isAr ? 'الإجمالي' : 'Total'}
                  </th>
                  <th className='small' style={{ minWidth: '180px' }}>
                    {isAr ? 'التفاصيل' : 'Details'}
                  </th>
                </tr>
              </thead>

              <tbody>
                {!loading && items.length > 0 ? (
                  items.map((r, idx) => {
                    const branchName = isAr ? r.branchName2 || r.branchName : r.branchName || r.branchName2;
                    return (
                      <tr key={`${r.purId || idx}`} className='align-middle text-center'>
                        {/* Branch */}
                        <td className='text-start'>
                          <span className='fw-medium '>{safe(branchName)}</span>
                        </td>

                        {/* PO Info */}
                        <td>
                          <span className='fw-bold text-primary'>{safe(r.purCode)}</span>
                        </td>
                        <td>
                          <span className='small'>{r.purDate ? r.purDate.split('T')[0] : '—'}</span>
                        </td>
                        <td className='text-end fw-bold '>{formatNumber(r.purAmount)}</td>

                        {/* Receipts */}
                        <td>
                          <span className='badge bg-info'>{r.receiptsCount}</span>
                        </td>
                        <td className='text-start'>{renderListBadges(r.receiptList, 'receipt')}</td>

                        {/* Invoices */}
                        <td>
                          <span className='badge bg-warning '>{r.invoicesCount}</span>
                        </td>
                        <td className='text-end fw-bold text-warning'>{formatNumber(r.invoicesTotal)}</td>
                        <td className='text-start'>{renderListBadges(r.invoiceList, 'invoice')}</td>

                        {/* Payments */}
                        <td>
                          <span className='badge bg-success'>{r.paymentsCount}</span>
                        </td>
                        <td className='text-end fw-bold text-success'>{formatNumber(r.paymentsTotal)}</td>
                        <td className='text-start'>{renderListBadges(r.paymentList, 'payment')}</td>
                      </tr>
                    );
                  })
                ) : !loading && !supplierFilter ? (
                  <tr>
                    <td colSpan={12} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-user-tie fa-3x mb-3 opacity-50 text-danger'></i>
                        <h6 className='text-danger'>
                          {isAr ? 'الرجاء اختيار المورد أولاً' : 'Please select a supplier first'}
                        </h6>
                      </div>
                    </td>
                  </tr>
                ) : !loading ? (
                  <tr>
                    <td colSpan={12} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-link fa-3x mb-3 opacity-50'></i>
                        <h6>{t('NoData')}</h6>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>

              {!loading && items.length > 0 && (
                <tfoot className='fw-bold bg-secondary bg-opacity-30 text-center'>
                  <tr>
                    <td colSpan={3} className='text-end text-primary'>
                      <span className='h6 m-0'>{isAr ? 'إجمالي أوامر الشراء:' : 'Total PUR:'}</span>
                    </td>
                    <td className='text-end text-primary'>
                      <span className='h6 m-0'>{formatNumber(totals.totalPurAmount)}</span>
                    </td>

                    <td colSpan={2}></td>

                    <td className='text-end text-warning'>
                      <span className='h6 m-0'>{isAr ? 'إجمالي الفواتير:' : 'Total INV:'}</span>
                    </td>
                    <td className='text-end text-warning'>
                      <span className='h6 m-0'>{formatNumber(totals.totalInvoices)}</span>
                    </td>

                    <td colSpan={1}></td>

                    <td className='text-end text-success'>
                      <span className='h6 m-0'>{isAr ? 'إجمالي المدفوعات:' : 'Total PAY:'}</span>
                    </td>
                    <td className='text-end text-success'>
                      <span className='h6 m-0'>{formatNumber(totals.totalPayments)}</span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />
            {!loading && items.length > 0 && (
              <div className='text-muted small'>
                <i className='fas fa-info-circle me-1'></i>
                {isAr ? 'عرض' : 'Showing'} {items.length} {isAr ? 'من' : 'of'} {meta?.totalRows || 0}{' '}
                {isAr ? 'سجل' : 'records'}
              </div>
            )}
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'مشتريات المورد المترابطة' : 'Supplier Pur Linked')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default SupplierPurLinkedGrouped_Paged;
